<template>
  <DModal
    :show-close="false"
    :isBgOverlay="true"
    @close="close"
    :appendToBody="true"
    :placeInTop="true"
    style="z-index: 12312312312312"
  >
    <div class="d-modal transfer-rest text-dark" slot="insertComponent">
      <div class="d-modal__header">
        <h2>Overfør positiv rest til brik</h2>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="close">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="d-modal__content">
        <div class="transfer-rest__table-scroll">
          <table class="do-table do-table--striped">
            <thead>
              <tr>
                <th class="transfer-rest__check">
                  <input
                    type="checkbox"
                    :checked="isAllSelected"
                    :disabled="users.length === 0"
                    @change="toggleAll">
                </th>
                <th>Navn</th>
                <th class="transfer-rest__narrow">Arbejdstid</th>
                <th class="transfer-rest__narrow">Rest</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="users.length === 0">
                <td colspan="4"><i>Ingen personer har positiv rest</i></td>
              </tr>
              <tr
                v-for="user in users"
                :key="user.id"
                class="transfer-rest__row"
                @click="toggleUser(user.id)">
                <td class="transfer-rest__check">
                  <input type="checkbox" :checked="isSelected(user.id)" @click.stop="toggleUser(user.id)">
                </td>
                <td><strong>{{ user.name }}</strong></td>
                <td class="transfer-rest__narrow">{{ formatHours(user.assigned_total_hours) }}</td>
                <td class="transfer-rest__narrow">{{ formatHours(user.rest) }}</td>
              </tr>
            </tbody>
            <tfoot v-if="users.length > 0">
              <tr>
                <td colspan="3"><strong>{{ selectedUsers.length }} af {{ users.length }} valgt</strong></td>
                <td class="transfer-rest__narrow"><strong>{{ formatHours(selectedRestSum) }}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="transfer-rest__target">
          <div class="transfer-rest__modes">
            <button
              class="do-bttn"
              :class="{'do-bttn--active': mode === 'new'}"
              @click="mode = 'new'">
              Opret ny brik
            </button>
            <button
              class="do-bttn"
              :class="{'do-bttn--active': mode === 'existing'}"
              :disabled="existingBriks.length === 0"
              @click="mode = 'existing'">
              Vælg eksisterende brik
            </button>
          </div>

          <template v-if="mode === 'new'">
            <div class="transfer-rest__fields">
              <div class="transfer-rest__field transfer-rest__field--wide">
                <label class="d-label">Brikkens titel</label>
                <input type="text" class="d-input" v-model="newBrik.name" placeholder="Eks. Rest til fordeling">
              </div>
              <div class="transfer-rest__field">
                <label class="d-label">Type</label>
                <select v-model="newBrik.subject_type" class="d-input__select">
                  <option v-for="t in allowedTypes" :key="t.value" :value="t.value">{{ t.name }}</option>
                </select>
              </div>
            </div>
            <div class="transfer-rest__field">
              <label class="d-label">Farve</label>
              <ColorPicker :initialColor="newBrik.color" @changedColor="newBrik.color = $event"></ColorPicker>
            </div>
          </template>

          <template v-else>
            <div class="transfer-rest__field">
              <label class="d-label">Brik</label>
              <select v-model="selectedBrikKey" class="d-input__select">
                <option :value="null" disabled>-- Vælg brik --</option>
                <option v-for="brik in existingBriks" :key="brik.key" :value="brik.key">
                  {{ brik.name }} ({{ typeName(brik.subject_type) }})
                </option>
              </select>
              <p class="transfer-rest__hint">
                Resten lægges oven i personens nuværende timer på brikken. Personer, der ikke har brikken, får den tilføjet.
              </p>
            </div>
          </template>
        </div>
      </div>

      <div class="d-modal__buttons">
        <button class="do-bttn" :disabled="isSaving" @click="close">Annullér</button>
        <button
          class="do-bttn do-bttn--submit"
          :class="{'do-bttn--submit--loading': isSaving}"
          :disabled="!canSubmit"
          @click="submit">
          Overfør til {{ selectedUsers.length }} {{ selectedUsers.length === 1 ? 'person' : 'personer' }}
        </button>
      </div>
    </div>
  </DModal>
</template>

<script>
import DModal from '../common/DModal.vue'
import ColorPicker from '../Subjects/ColorPicker'
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import {getVHoursForSubject, sameCalendarId} from '../../utils/preparationFactor'

export default {
  components: {DModal, ColorPicker},
  props: {
    users: {
      type: Array,
      default: () => []
    },
    schoolId: {
      default: null
    },
    schoolYearId: {
      default: null
    },
    isLocalPoolActivated: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedUserIds: [],
      mode: 'new',
      selectedBrikKey: null,
      isSaving: false,
      newBrik: {
        name: '',
        subject_type: 'additional_tasks',
        color: '#999999'
      }
    }
  },
  computed: {
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash || {}
    },
    allowedTypes () {
      const types = [
        {value: 'additional_tasks', name: 'Øvrige opgaver'},
        {value: 'club', name: 'SFO'}
      ]
      if (this.isLocalPoolActivated) {
        types.push({value: 'pool', name: this.$store.getters.localPoolTitle || 'Lokalpulje'})
      }
      return types
    },
    selectedUsers () {
      return this.users.filter(user => this.selectedUserIds.indexOf(user.id) > -1)
    },
    isAllSelected () {
      return this.users.length > 0 && this.selectedUsers.length === this.users.length
    },
    selectedRestSum () {
      return this.selectedUsers.reduce((sum, user) => sum + user.rest, 0)
    },
    existingBriks () {
      const allowed = this.allowedTypes.map(t => t.value)
      const groups = {}
      ;(this.$store.state.subjects.subjects || []).forEach(subject => {
        if (allowed.indexOf(subject.subject_type) === -1 || subject.task_overview === false) {
          return
        }
        const key = subject.subject_type + ';' + subject.name
        if (!groups[key]) {
          groups[key] = {
            key: key,
            name: subject.name,
            subject_type: subject.subject_type,
            subjects: []
          }
        }
        groups[key].subjects.push(subject)
      })
      return Object.keys(groups)
        .map(key => groups[key])
        .sort((a, b) => a.name.localeCompare(b.name, 'da-DK'))
    },
    selectedBrik () {
      return this.existingBriks.find(brik => brik.key === this.selectedBrikKey) || null
    },
    canSubmit () {
      if (this.isSaving || this.selectedUsers.length === 0) {
        return false
      }
      if (this.mode === 'new') {
        return this.newBrik.name.trim().length > 0 && !!this.newBrik.subject_type
      }
      return !!this.selectedBrik
    }
  },
  methods: {
    close () {
      if (this.isSaving) {
        return
      }
      this.$emit('close')
    },
    formatHours (value) {
      const num = parseFloat(value)
      return isNaN(num) ? '' : num.toFixed(1)
    },
    roundHours (value) {
      return Math.round(value * 100) / 100
    },
    typeName (subjectType) {
      const type = this.allowedTypes.find(t => t.value === subjectType)
      return type ? type.name : subjectType
    },
    isSelected (userId) {
      return this.selectedUserIds.indexOf(userId) > -1
    },
    toggleUser (userId) {
      const idx = this.selectedUserIds.indexOf(userId)
      if (idx > -1) {
        this.selectedUserIds.splice(idx, 1)
      } else {
        this.selectedUserIds.push(userId)
      }
    },
    toggleAll () {
      this.selectedUserIds = this.isAllSelected ? [] : this.users.map(user => user.id)
    },
    isUserCalendar (calId) {
      const cal = this.allCalendarsInHash[calId]
      return !!(cal && cal.type === 'user')
    },
    subjectHasUser (subject, calId) {
      if (sameCalendarId(subject.calendar_id, calId)) {
        return true
      }
      return (subject.cals || []).some(cal => cal.calendar_type === 'user' && sameCalendarId(cal.id, calId))
    },
    apiCall (fn) {
      return new Promise((resolve, reject) => {
        fn(data => {
          if (data && data.status === 'error') {
            reject(data)
            return
          }
          resolve(typeof data === 'string' ? JSON.parse(data) : data)
        }, reject)
      })
    },
    subjectPayload (template, calId, hours) {
      return {
        name: template.name,
        color: template.color,
        subject_type: template.subject_type,
        default_duration: template.default_duration != null ? template.default_duration : 60,
        preparation: template.preparation != null ? template.preparation : null,
        can_schedule: !!template.can_schedule,
        from_date: template.from_date || null,
        to_date: template.to_date || null,
        calendar_id: calId,
        calendar_ids: [],
        hours: hours
      }
    },
    createSubjects (payloads) {
      if (payloads.length === 0) {
        return Promise.resolve()
      }
      return this.apiCall((ok, fail) => docendo.bulkCreateSubjects(this.schoolId, this.schoolYearId, payloads, ok, fail))
    },
    setUserHours (subject, calId, hours) {
      return this.apiCall((ok, fail) => docendo.setHoursForTeacherOnSubject(subject.id, subject.school_id, subject.school_year_id, calId, hours, ok, fail))
    },
    addRestToSubject (subject, calId, rest) {
      const hours = this.roundHours(getVHoursForSubject(subject, calId) + rest)
      const hasCalendarHours = (subject.subject_calendars || []).some(sc => sc.hours != null)
      if (sameCalendarId(subject.calendar_id, calId) && !hasCalendarHours) {
        return this.apiCall((ok, fail) => docendo.bulkUpdateSubjects(subject.school_id, [subject.id], {hours: hours}, ok, fail))
      }
      return this.setUserHours(subject, calId, hours)
    },
    addUserToSharedSubject (subject, calId, rest) {
      return this.apiCall((ok, fail) => docendo.bulkUpdateSubjects(subject.school_id, [subject.id], {add_calendar_id: calId}, ok, fail))
        .then(() => this.setUserHours(subject, calId, this.roundHours(rest)))
    },
    transferToNewBrik () {
      const template = {
        name: this.newBrik.name.trim(),
        color: this.newBrik.color,
        subject_type: this.newBrik.subject_type,
        can_schedule: false
      }
      const payloads = this.selectedUsers.map(user => this.subjectPayload(template, user.user_calendar_id, this.roundHours(user.rest)))
      return this.createSubjects(payloads).then(() => ({failed: 0}))
    },
    transferToExistingBrik () {
      const subjects = this.selectedBrik.subjects
      const template = subjects.find(s => this.isUserCalendar(s.calendar_id)) || subjects[0]
      const sharedSubject = subjects.find(s => s.calendar_id && !this.isUserCalendar(s.calendar_id))
      const toCreate = []
      const operations = []
      this.selectedUsers.forEach(user => {
        const calId = user.user_calendar_id
        const subject = subjects.find(s => this.subjectHasUser(s, calId))
        if (subject) {
          operations.push(this.addRestToSubject(subject, calId, user.rest))
        } else if (sharedSubject) {
          operations.push(this.addUserToSharedSubject(sharedSubject, calId, user.rest))
        } else {
          toCreate.push(this.subjectPayload(template, calId, this.roundHours(user.rest)))
        }
      })
      operations.push(this.createSubjects(toCreate))
      return Promise.all(operations.map(op => op.then(() => true, () => false)))
        .then(results => ({failed: results.filter(ok => !ok).length}))
    },
    submit () {
      if (!this.canSubmit) {
        return
      }
      this.isSaving = true
      const transfer = this.mode === 'new' ? this.transferToNewBrik() : this.transferToExistingBrik()
      transfer.then(result => {
        this.isSaving = false
        if (result.failed > 0) {
          eventbus.toast = {text: 'Resten kunne ikke overføres for alle personer - tjek tallene', success: false}
        } else {
          eventbus.toast = {text: 'Resten er overført til brikken', success: true}
        }
        this.$emit('transferred')
        this.$emit('close')
      }, () => {
        this.isSaving = false
        eventbus.toast = {text: 'Kunne ikke overføre resten - prøv igen', success: false}
      })
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.transfer-rest {
  width: 720px;
  max-width: calc(100vw - 2rem);
}

.d-modal__header {
  margin-bottom: 0;
}

.d-modal__content {
  padding: 1rem;
  max-height: calc(100vh - 150px);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.transfer-rest__table-scroll {
  max-height: 40vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid #e3e6ea;
  border-radius: 4px;
}

.transfer-rest__table-scroll table {
  width: 100%;
}

.transfer-rest__table-scroll thead {
  display: table-header-group;
}

.transfer-rest__table-scroll tbody tr {
  display: table-row;
}

.transfer-rest__table-scroll thead,
.transfer-rest__table-scroll tfoot {
  position: sticky;
  z-index: 2;
  background: #f6f9fc;
}

.transfer-rest__table-scroll thead {
  top: 0;
  box-shadow: inset 0 -1px 0 #e3e6ea;
}

.transfer-rest__table-scroll tfoot {
  bottom: 0;
  box-shadow: inset 0 1px 0 #e3e6ea;
}

.transfer-rest__row {
  cursor: pointer;
}

.transfer-rest__check {
  width: 40px;
  text-align: center;
}

.transfer-rest__narrow {
  width: 120px;
  text-align: right;
}

.transfer-rest__target {
  margin-top: 1.5rem;
}

.transfer-rest__modes {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.do-bttn--active {
  background-color: var(--dark-blue);
  border-color: var(--dark-blue);
  color: #ffffff;
}

.transfer-rest__fields {
  display: flex;
  gap: 1rem;
}

.transfer-rest__field {
  flex: 1;
  margin-bottom: 1rem;
}

.transfer-rest__field--wide {
  flex: 2;
}

.transfer-rest__fields .transfer-rest__field {
  display: flex;
  flex-direction: column;
}

.transfer-rest__fields .d-input,
.transfer-rest__fields .d-input__select {
  flex-grow: 1;
}

.transfer-rest__hint {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: #728195;
}

.d-modal__buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #eee;
}
</style>



// WEBPACK FOOTER //
// AssignHoursTransferRestModal.vue?31322368